import { useAtomValue } from 'jotai'
import React from 'react'

import { ExtensionInstance } from '@mangovoice/types'

import { extensionsListAtom } from '../../atoms'
import type { SwitchboardTranslateFn } from '../../types'
import {
  getDirectLineExtensions,
  getSimpleDialExtensions,
  getTransferableExtensions,
  getTransferRolloverExtensions,
  getVoicemailExtensions,
} from '../../utils/extensions'
import { text } from '../../utils/text'
import { NodeSelect } from './NodeSelect'

/**
 * Filter modes for extension selector.
 * Determines which extensions are shown in the dropdown.
 */
export type ExtensionFilterMode =
  | 'transferable' // Default: ExtensionLine, ExtensionRingGroup, ExtensionSharedVoicemailBox (active only)
  | 'simple_dial' // Excludes 6 types: CallQueue, ConferenceRoom, DialPlan, SharedVoicemailBox, System, VirtualFaxMachine (active only)
  | 'direct_line' // Only ExtensionLine type (active only)
  | 'transfer_rollover' // Only active ExtensionLine type (most restrictive)
  | 'voicemail' // Voicemail-enabled: ExtensionSharedVoicemailBox OR voicemail_enabled=true (active only)
  | 'all' // All extensions (no filtering)

export interface ExtensionSelectorProps {
  /** Current selected extension ID */
  value?: string
  /** Callback when extension selection changes */
  onChange: (extensionId: string | undefined) => void
  /** Placeholder text for the select */
  placeholder?: string
  /** Label for the floating label */
  label?: string
  /** Filter mode for extensions (default: 'transferable') */
  filterMode?: ExtensionFilterMode
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
  // Note: extensionsList is provided via Jotai atoms (extensionsListAtom)
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
}

/**
 * Reusable extension selector dropdown component with floating label.
 * Supports different filter modes for different node types.
 */
export const ExtensionSelector: React.FC<ExtensionSelectorProps> = ({
  value = '',
  onChange,
  placeholder: placeholderProp,
  label: labelProp,
  filterMode = 'transferable',
  t,
}) => {
  const placeholder =
    placeholderProp ??
    text(t, 'switchboard.core.selectExtension', 'Select extension...')
  const label =
    labelProp ?? text(t, 'switchboard.core.extensionLabel', 'Extension')
  const extensionsList = useAtomValue(extensionsListAtom)

  const selectedExtension = React.useMemo(() => {
    if (!value || value.trim() === '' || !extensionsList) return null
    return extensionsList.find((ext) => String(ext.id) === String(value))
  }, [extensionsList, value])

  const availableExtensions = React.useMemo(() => {
    const filtered: ExtensionInstance[] = (() => {
      switch (filterMode) {
        case 'simple_dial':
          return getSimpleDialExtensions(extensionsList, value)
        case 'direct_line':
          return getDirectLineExtensions(extensionsList, value)
        case 'transfer_rollover':
          return getTransferRolloverExtensions(extensionsList, value)
        case 'voicemail':
          return getVoicemailExtensions(extensionsList, value)
        case 'all':
          return extensionsList || []
        case 'transferable':
        default:
          return getTransferableExtensions(extensionsList)
      }
    })()

    // If a selected extension exists but is not in the filtered list, include it
    // to ensure the selected extension displays correctly (preserves existing selections)
    if (selectedExtension) {
      const isInFiltered = filtered.some(
        (ext) => ext.id === selectedExtension.id,
      )
      if (!isInFiltered) {
        return [selectedExtension, ...filtered]
      }
    }

    return filtered
  }, [extensionsList, selectedExtension, filterMode, value])

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange(e.target.value || undefined)
    },
    [onChange],
  )

  return (
    <NodeSelect label={label} value={value} onChange={handleChange}>
      <option value="">{placeholder}</option>
      {availableExtensions.map((ext) => (
        <option key={ext.id} value={String(ext.id)}>
          {ext.number} -{' '}
          {ext.name || text(t, 'switchboard.core.unnamed', 'Unnamed')}
        </option>
      ))}
    </NodeSelect>
  )
}
